import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import PropTypes from "prop-types";
import shallow from "zustand/shallow";
import { Box, Flex, PortalManager, Tooltip, useDisclosure } from "@chakra-ui/react";
import { useToast } from "@/components/ui/Toast";
import { UNEXPECTED_ERROR } from "@/utils/envUtils";

import _ from "lodash";
import moment from "moment";
import url from "url";

import { sendCloseTab } from "@/js/api/eventApi";
import { createOrUpdateFocus, endFocus, getFocusesAppliedToClass } from "@/js/api/focus";
import { unpauseAllStudents } from "@/api/pause";

import { arrayMove } from "@/js/utils/arrayUtils";
import p2pManager from "@/js/utils/p2pManager";
import { generateSessionStatusTxt } from "@/js/utils/timeUtils";
import { userDisplayText } from "@/js/utils/userUtils";
import * as windowUtils from "@/js/utils/windowUtils";
import { isChatFeatureEnabled, selectChatDisabled } from "@/js/utils/chatUtils";

import useStore from "@/stores/index";
import { getFeatures } from "@/stores/classwizeConfigStore";
import { useSelectedClassroomStore } from "@/stores/v2/selectedClassroomStore";
import { useSeatPlanStore } from "@/stores/v2/seatPlanStore";
import { useLicenseStore } from "@/stores/v2/licenseStore";
import { useFocusStore } from "@/stores/v2/focusStore";
import { useClassroomSessionStore } from "@/stores/v2/classroomSessionStore";
import { useClassroomPolicyStore } from "@/stores/v2/classroomPolicyStore";
import { useUserSessionStore } from "@/stores/v2/userSessionStore";
import { useSignatureStore } from "@/stores/v2/signatureStore";
import { useAccountSettingsUse24Hour } from "@/query/accountSettingsQuery";
import { useScreenshareStore } from "@/stores/v2/screenshareStore";

import Nav from "@/js/components/Nav";
import BypassModal from "@/js/components/BypassModal";
import { ClassroomPromo } from "@/js/components/ClassroomPromo";
import ClassroomStartSession from "@/js/components/ClassroomStartSession";
import CreatePolicy from "@/js/components/CreatePolicy";
import EditClassButton from "@/js/components/EditClassButton";
import EndClassButton from "@/js/components/EndClassButton";
import ErrorBoundary from "@/components/ErrorBoundary";
import ClassroomEditSession from "@/js/components/ClassroomEditSession";
import RegularClassroomUser from "@/js/components/RegularClassroomUser";
import ClassroomChat from "@/js/components/ClassroomChat";
import P2PDisplayModal from "@/js/components/P2PDisplayModal";
import AgentP2PModal from "@/js/components/P2PDisplayModal/AgentP2PModal";
import PolicyDrawer from "@/js/components/PolicyDrawer";
import RewardModal from "@/js/components/RewardModal";
import Screenshare from "@/components/Screenshare";
import { SendNewTabModal } from "@/js/components/SendNewTabModal";
import { StartClassButton } from "@/js/components/StartClassButton";
import { TabBar } from "@/js/components/TabBar";
import { useQueryClient } from "@tanstack/react-query";
import {
	computeShowRegularClassroomStudentTiles,
	invalidateStudentClaimsQuery,
	studentClaimsToMapByStudentId,
	useClaimStudentsMutation,
	useReleaseStudentClaimMutation,
	useStudentClaimsQuery,
} from "@/query/studentClaimsQuery";
import { getStudentClaimBannerViewModel } from "@/js/components/RegularClassroom/studentClaimBannerUtils";
import { StudentClaimsModal } from "@/js/components/StudentClaims/StudentClaimsModal";
import {
	getSignaturesForFocusResourcePicker,
	transformFocusDtoToEditingFocus,
} from "@/components/FocusLibraryDrawer/FocusResourcePicker/utils";
import { FocusAndFlexibleGridBar } from "@/js/components/RegularClassroom/FocusAndFlexibleGridBar";
import { SeatPlanView } from "@/components/SeatPlan/SeatPlanView";
import { SeatPlanEmptyState } from "@/components/SeatPlan/SeatPlanEmptyState";
import { FocusLibraryDrawer } from "@/components/FocusLibraryDrawer";
import { ActionButton } from "@/js/components/Classrooms/ActionButton";
import ReportsDropdown from "@/js/components/ReportsDropdown";
import ClassToolsDropdown from "@/components/ClassToolsDropdown";
import { PauseInternetDrawer } from "@/js/components/PauseInternetDrawer";
import { envConfig } from "@/config";
import { buildClassRulePrefill, getFocusedStudentIdsFromAppliedFocuses } from "./utils";
import { getClassroomDisplayName } from "@/utils/classroomUtils";
import { reportStudentClaimsConfirmedAnalytics } from "@/utils/reportStudentClaimsConfirmedAnalytics";
import { captureStudentConflictDetectedEvent, captureTabLimitRemovedEvent } from "@/utils/posthogUtils";
import { useOnFirstStudentClaimsDetected } from "@/hooks/useOnFirstStudentClaimsDetected";
import { ModifyStudentsModal } from "@/components/ModifyStudentsModal";
import { ConfirmEndFocusModal } from "@/components/ConfirmEndFocusModal";
import { sortRegularClassroomStudents } from "@/js/components/RegularClassroom/sortRegularClassroomStudents";
import { HackyGetClassroomSessionId, useStudentState } from "@/stores/v2/studentStateStore";
import { ChakraFAIcon } from "@/components/ui/Icon";
import { ClassroomToolsContext } from "@/contexts/ClassroomToolsContext";
import { transformSelectedResourcesToMatchAPIContract } from "@/components/FocusLibraryDrawer/utils";
import { useTranslation } from "react-i18next";
import { FeatureFlag } from "@/api/classroomFeatureFlags";
import { useFeatureFlagsQuery } from "@/query/featureFlagsQuery";
import { RulesLibraryDrawer } from "@/components/RulesLibraryDrawer";
import { useActiveRulesCount } from "@/components/RulesLibraryDrawer/ActiveRules/useActiveRulesCount";
import { sortSeatRosterIds } from "@/components/SeatPlan/seatLayout";

export const windowLimit = 3;

export const RegularClassroom = (props) => {
	const { t } = useTranslation();

	const store = useStore();
	const focusStore = useFocusStore();
	const userSessionStore = useUserSessionStore();
	const classroomIdForChat = props.classroom?.name;
	const classroomPolicyStore = useClassroomPolicyStore();
	const lastPolicyValidationResponse = useClassroomPolicyStore((s) => s.lastPolicyValidationResponse);
	const screenshareStore = useScreenshareStore();
	const featureFlagsQuery = useFeatureFlagsQuery(userSessionStore.active_device ?? "");
	const showRulesLibraryDrawer = !!featureFlagsQuery.data?.includes(FeatureFlag.RULES_LIBRARY);
	const { signatures, appControls, findAppControl } = useSignatureStore(
		(state) => ({
			signatures: state.signatures,
			appControls: state.appControls,
			findAppControl: state.findAppControl,
		}),
		shallow
	);

	const [activeClassroomSourceId] = useState(userSessionStore.getActiveGroup());

	const [view, setView] = useState("screens"); // enum of ('screens', 'tabs')
	const [classOpenTime, setClassOpenTime] = useState(Math.round(Date.now() / 1000));
	const [showAllWindows, setShowAllWindows] = useState(false);
	const features = getFeatures();
	const [classroomPolicy, setClassroomPolicy] = useState(classroomPolicyStore);
	const nativeAppControlsDb = useMemo(
		() => ({
			nativeAppControls: appControls,
			find: findAppControl,
		}),
		[appControls, findAppControl]
	);
	const [classExpiryStr, setClassExpiryStr] = useState("");
	const [isEditingRule, setIsEditingRule] = useState(false);
	const [currentEditingRule, setCurrentEditingRule] = useState({});
	const [currentEditingNativeAppControl, setCurrentEditingNativeAppControl] = useState({});
	const [validationResponseData, setValidationResponseData] = useState({});

	// derived
	const [isPauseActive, setIsPauseActive] = useState(classroomPolicyStore.isPauseActive());
	// modals
	const [showBypassModal, setShowBypassModal] = useState(false);
	const [showRewardModal, setShowRewardModal] = useState(false);
	const [showStartClassModal, setShowStartClassModal] = useState(false);
	const [showEditClassModal, setShowEditClassModal] = useState(false);
	const [showPolicyModal, setShowPolicyModal] = useState(false);

	const [showPauseDrawer, setShowPauseDrawer] = useState(false);

	// open tab
	const [showOpenTabModal, setShowOpenTabModal] = useState(false);
	const [openTabUser, setOpenTabUser] = useState(null);

	// policy side drawer
	const [showPolicyDrawer, setShowPolicyDrawer] = useState(false);
	// errors
	const [startClassSessionError, setStartClassSessionError] = useState(false);

	const [p2pModalOpen, setP2pModalOpen] = useState(false);
	const [p2pModalUser, setP2pModalUser] = useState();
	const [p2pAgentId, setP2pAgentId] = useState();
	const [p2pChromeWindowId, setP2pChromeWindowId] = useState();
	const [p2pModalSelectedTab, setP2pModalSelectedTab] = useState();
	const [p2pModalFollowActiveTab, setP2pModalFollowActiveTab] = useState(false);
	const [p2pModalStudentIsFocused, setP2pModalStudentIsFocused] = useState(false);
	const [screenshareModalOpen, setScreenshareModalOpen] = useState(false);

	const modifyStudentsModal = useDisclosure();
	const focusLibraryDrawer = useDisclosure();
	const rulesLibraryDrawer = useDisclosure();
	const [classRulePrefill, setClassRulePrefill] = useState(undefined);

	const confirmEndFocusModal = useDisclosure();
	const [confirmEndingFocusName, setConfirmEndingFocusName] = useState("");

	const [users, setUsers] = useState(props.users);
	const [classroom, setClassroom] = useState(props.classroom);
	const [studentClaimsModalOpen, setStudentClaimsModalOpen] = useState(false);
	const activeRulesCount = useActiveRulesCount({
		enabled: showRulesLibraryDrawer,
		applianceId: userSessionStore.active_device ?? "",
	});

	const chatDisabled = useStore(useCallback((state) => selectChatDisabled(state, classroom.stableIdV1), [classroom.stableIdV1]));

	const [tabItemLimit, setTabItemLimit] = useState(5); // variable tab items to display on student tile

	/**
	 * @type {[import("../../../components/FocusLibraryDrawer/FocusPage").EditingFocus[], React.Dispatch<React.SetStateAction<EditingFocus[]>>]}
	 */
	const [appliedFocuses, setAppliedFocuses] = useState([]);

	// Tab Limit state
	const [tabLimitModalOpen, setTabLimitModalOpenState] = useState(false);
	const [tabLimitOpenedViaEdit, setTabLimitOpenedViaEdit] = useState(false);
	const setTabLimitModalOpen = useCallback((open, viaEdit = false) => {
		if (open) {
			setTabLimitOpenedViaEdit(!!viaEdit);
		}
		setTabLimitModalOpenState(open);
	}, []);
	const tabLimit = classroomPolicyStore.getTabLimit();

	const { successToast, errorToast } = useToast();

	const isClassActive = props.isClassStarted;

	// Track previous class active state to detect transitions
	const prevIsClassActiveRef = useRef(isClassActive);

	// Check if any focuses are applied and update this state when appliedFocuses changes
	const [focusIsApplied, setFocusIsApplied] = useState(false);
	const [focusedStudentIds, setFocusedStudentIds] = useState([]);
	const [activeFocusIsLocked, setActiveFocusIsLocked] = useState(false);

	// useRef to track if the student claim button has been clicked
	const isStudentClaimClicked = useRef(false);
	/** Covers POST + toasts + claims invalidation. `claimStudentsMutation.isLoading` alone goes false before refetch, so the claim label flashes; refs do not trigger re-renders so `&& isStudentClaimClicked.current` cannot extend loading UI. */
	const [isStudentClaimFlowActive, setIsStudentClaimFlowActive] = useState(false);

	// When the class becomes active, set the class open time, for reporting to the studentStateStore.
	useEffect(() => {
		setClassOpenTime(Math.round(Date.now() / 1000));
	}, [props.isClassStarted]);

	useEffect(() => {
		setFocusIsApplied(appliedFocuses.length > 0);
		setFocusedStudentIds(getFocusedStudentIdsFromAppliedFocuses(appliedFocuses));
		setActiveFocusIsLocked(appliedFocuses.length > 0 && appliedFocuses[0].tabsLocked === true);
	}, [appliedFocuses]);

	const [isUpdatingFocus, setIsUpdatingFocus] = useState(false);

	function handleEndFocus(focusId) {
		setIsUpdatingFocus(true);
		endFocus(focusId, classroom.stableIdV1, userSessionStore.getTeacherId())
			.then(() => {
				const libItem = focusStore.getLibraryItemById(focusId);
				if (libItem) {
					focusStore.upsertFocusItemInLibrary({ ...libItem, active: false });
				}
				successToast({
					title: t("classroomDetails.focusEnded"),
					isClosable: true,
				});
				setAppliedFocuses(appliedFocuses.filter((focus) => focus.id !== focusId));
			})
			.catch((err) => {
				console.error("Failed to end focus", err);
				errorToast({
					title: t("classroomDetails.failedToEndFocus"),
					description: UNEXPECTED_ERROR,
					isClosable: true,
				});
			})
			.finally(() => {
				setIsUpdatingFocus(false);
			});
	}

	/**
	 * Handle toggling focus for a student. This function looks at the applied focus and determines whether or not the given student is in the studentIds array.
	 * If the student is in the array, the focus is updated to remove the student from it. If the student is not in the array, the student is added to the focus.
	 * @param {string} studentId - The ID of the student to toggle focus for
	 * @param {string} focusId - The ID of the focus to toggle focus for
	 * @returns {void}
	 */
	function handleToggleFocusForStudent(studentId, focusId) {
		/**
		 * @type {import("../../../components/FocusLibraryDrawer/FocusPage").EditingFocus}
		 */
		const appliedFocus = appliedFocuses.find((focus) => focus.id === focusId);
		if (!appliedFocus) {
			console.error("Focus not found", focusId);
			return;
		}

		const updatedStudentIds = [...appliedFocus.studentIds]; // Make a copy, not a reference
		const studentIndex = updatedStudentIds.indexOf(studentId);
		if (studentIndex >= 0) {
			updatedStudentIds.splice(studentIndex, 1);
		} else {
			updatedStudentIds.push(studentId);
		}

		if (updatedStudentIds.length == 0) {
			setConfirmEndingFocusName(appliedFocus.name);
			confirmEndFocusModal.onOpen();
			return;
		}

		handleUpdateActiveFocus(appliedFocus, updatedStudentIds);
	}

	/**
	 * Used to update the students array for a focus that is currently applied to the classroom.
	 * @param {import("../../../components/FocusLibraryDrawer/FocusPage").EditingFocus} appliedFocus
	 * @param {string[]} studentIds
	 * @returns {void}
	 */
	function handleUpdateActiveFocus(appliedFocus, studentIds) {
		setIsUpdatingFocus(true);

		createOrUpdateFocus(
			transformSelectedResourcesToMatchAPIContract(appliedFocus.resources),
			appliedFocus.tabsLocked || false,
			true,
			!appliedFocus.isOnTheFly,
			userSessionStore.getTeacherId(),
			appliedFocus.name,
			selectedClassroomStore.stableIdV1,
			studentIds,
			appliedFocus.id
		)
			.then((response) => {
				const updatedFocus = transformFocusDtoToEditingFocus(response, getSignaturesForFocusResourcePicker(signatures));
				setAppliedFocuses(appliedFocuses.map((focus) => (focus.id === appliedFocus.id ? updatedFocus : focus)));

				successToast({
					title: t("classroomDetails.focusUpdated"),
					isClosable: true,
				});
			})
			.catch((err) => {
				console.error("Failed to update focus", err);
				errorToast({
					title: t("classroomDetails.failedToUpdateFocus"),
					description: UNEXPECTED_ERROR,
					isClosable: true,
				});
			})
			.finally(() => {
				setIsUpdatingFocus(false);
			});
	}

	/**
	 * Adds a set of students to the focus currently applied to the class, merging them with the students
	 * already in that focus (rather than replacing the list). Used by the Seat Plan selection toolbar so
	 * "Add N to focus" tops up the active focus instead of restarting it.
	 * @param {string[]} studentIds - Stable IDs (stableIdV1) of the students to add
	 * @returns {void}
	 */
	function handleAddStudentsToActiveFocus(studentIds) {
		const appliedFocus = appliedFocuses[0];
		if (!appliedFocus) {
			console.error("No active focus to add students to");
			return;
		}

		const merged = Array.from(new Set([...appliedFocus.studentIds, ...studentIds]));
		// Nothing new to add (every selected student is already focused).
		if (merged.length === appliedFocus.studentIds.length) return;

		handleUpdateActiveFocus(appliedFocus, merged);
	}

	/**
	 * Removes a set of students from the focus currently applied to the class. If this would empty the
	 * focus entirely, the end-focus confirmation modal is shown instead (mirroring the per-student toggle).
	 * @param {string[]} studentIds - Stable IDs (stableIdV1) of the students to remove
	 * @returns {void}
	 */
	function handleRemoveStudentsFromActiveFocus(studentIds) {
		const appliedFocus = appliedFocuses[0];
		if (!appliedFocus) {
			console.error("No active focus to remove students from");
			return;
		}

		const toRemove = new Set(studentIds);
		const remaining = appliedFocus.studentIds.filter((id) => !toRemove.has(id));
		// None of the selected students were in the focus.
		if (remaining.length === appliedFocus.studentIds.length) return;

		if (remaining.length === 0) {
			setConfirmEndingFocusName(appliedFocus.name);
			confirmEndFocusModal.onOpen();
			return;
		}

		handleUpdateActiveFocus(appliedFocus, remaining);
	}

	const fetchAppliedFocuses = useCallback(() => {
		const allSignatureResourceOptions = getSignaturesForFocusResourcePicker(signatures);

		getFocusesAppliedToClass(classroom.stableIdV1, useUserSessionStore.getState().getTeacherId())
			.then((responseBody) => {
				const transformedFocuses = responseBody.appliedFocuses.map((focus) =>
					transformFocusDtoToEditingFocus(focus, allSignatureResourceOptions)
				);
				setAppliedFocuses(transformedFocuses);
			})
			.catch((err) => {
				console.error("Failed to fetch applied focuses", err);
			});
	}, [classroom.stableIdV1, signatures]);

	useEffect(() => {
		fetchAppliedFocuses();
	}, [fetchAppliedFocuses]);

	const queryClient = useQueryClient();

	const claimStudentsMutation = useClaimStudentsMutation(classroom?.stableIdV1);
	const releaseStudentClaimMutation = useReleaseStudentClaimMutation(classroom?.stableIdV1);

	const studentClaimsQuery = useStudentClaimsQuery({
		classroomStableId: classroom.stableIdV1,
	});

	const studentClaimsByStudentId = useMemo(() => studentClaimsToMapByStudentId(studentClaimsQuery.data ?? []), [studentClaimsQuery.data]);

	const isMonitoringClassForStudentClaims = useStore((state) => state.classroom.getClassroomV2(classroom.stableIdV1).session?.isMonitoring);
	const showStudentTiles = computeShowRegularClassroomStudentTiles({
		isClassActive,
		isMonitoringClass: isMonitoringClassForStudentClaims,
		studentClaimsFetchedAfterMount: Boolean(studentClaimsQuery.isFetchedAfterMount),
	});

	const handleRefreshStudentClaims = useCallback(() => {
		const stableId = classroom?.stableIdV1;
		if (!isClassActive || !stableId) {
			return;
		}
		void invalidateStudentClaimsQuery(queryClient, stableId);
	}, [classroom?.stableIdV1, isClassActive, queryClient]);

	const handleClaimStudents = useCallback(
		async (studentIds) => {
			if (isStudentClaimClicked.current) {
				return;
			}

			if (studentIds.length === 0) {
				return;
			}

			// Set the flag to true to prevent duplicate claims
			isStudentClaimClicked.current = true;
			setIsStudentClaimFlowActive(true);

			const claimsSnapshot = studentClaimsQuery.data ?? [];
			try {
				await claimStudentsMutation.mutateAsync(studentIds);

				const loggedInTeacherUsername = userSessionStore.getActiveUser();
				const applianceId = userSessionStore.getActiveDevice();
				const managementClaimedByClassroom = getClassroomDisplayName(classroom);
				reportStudentClaimsConfirmedAnalytics(studentIds, claimsSnapshot, {
					loggedInTeacherUsername,
					applianceId,
					managementClaimedByClassroom,
				});

				if (studentIds.length > 1) {
					successToast({
						title: t("classroomDetails.studentsClaimedSuccess", { count: studentIds.length }),
						description: t("classroomDetails.studentsClaimedDescription", { count: studentIds.length }),
					});
				} else {
					const claim = claimsSnapshot.find((c) => c.studentId === studentIds[0]);
					successToast({
						title: t("classroomDetails.singleStudentManagingTitle", { studentName: claim?.studentName ?? "" }),
					});
				}
				await invalidateStudentClaimsQuery(queryClient, classroom.stableIdV1);
			} catch (error) {
				errorToast({
					title: t("classroomDetails.failedToClaimStudents"),
					description: error.message,
				});
			} finally {
				isStudentClaimClicked.current = false;
				setIsStudentClaimFlowActive(false);
			}
		},
		[claimStudentsMutation, successToast, errorToast, queryClient, classroom, studentClaimsQuery.data, userSessionStore, t]
	);
	const handleReleaseStudentClaim = useCallback(
		async (studentId) => {
			const claim = (studentClaimsQuery.data ?? []).find((c) => c.studentId === studentId);
			try {
				await releaseStudentClaimMutation.mutateAsync(studentId);
				successToast({
					title: t("classroomDetails.singleStudentReleasedTitle", { studentName: claim?.studentName ?? "" }),
				});
				await invalidateStudentClaimsQuery(queryClient, classroom.stableIdV1);
			} catch (error) {
				errorToast({
					title: t("errors.unexpectedError"),
					description: error.message,
				});
			}
		},
		[releaseStudentClaimMutation, successToast, errorToast, queryClient, classroom, studentClaimsQuery.data, t]
	);
	const isStudentClaimUiBusy = claimStudentsMutation.isLoading || isStudentClaimFlowActive;

	const hasClassroomLicense = useLicenseStore((state) => state.classroom);

	const selectedClassroomStore = useSelectedClassroomStore();
	const selectedClassroomStudents = useSelectedClassroomStore((s) => s.students);

	// Seat Plan view state + roster-driven hydration.
	const hydrateSeatPlan = useSeatPlanStore((s) => s.hydrate);
	const isSeatPlanHydrated = useSeatPlanStore((s) => s.hydratedClassroomStableId === props.classroom.stableIdV1);
	const hasSeatSelection = useSeatPlanStore((s) => s.selectedStudents.length > 0);
	const isDraggingSeats = useSeatPlanStore((s) => s.activeId !== null);
	const hasUnsavedArrangement = useSeatPlanStore((s) => s.hasUnsavedArrangement);
	const isMovingSeat = useSeatPlanStore((s) => s.pendingMoveIndex !== null);
	const isSeatSelectionMode = hasSeatSelection;
	const isClassToolsDisabled = isSeatSelectionMode;
	const classToolsDisabledTooltip = isSeatSelectionMode ? t("classTools.disabledSelectionTooltip") : undefined;

	const seatRosterStudents = selectedClassroomStudents;

	// Discard unsaved arrangement when leaving the classroom; the store is a global singleton
	useEffect(() => {
		return () => useSeatPlanStore.getState().resetTransientArrangement();
	}, []);

	useEffect(() => {
		if (isDraggingSeats || isMovingSeat || hasUnsavedArrangement || !isSeatPlanHydrated) return;
		const rosterIds = sortSeatRosterIds(seatRosterStudents ?? []);
		const { slots, columns, rows, cardWidth } = useSeatPlanStore.getState();
		hydrateSeatPlan({ slots, columns, rows, cardWidth }, rosterIds);
	}, [seatRosterStudents, isDraggingSeats, isMovingSeat, hasUnsavedArrangement, isSeatPlanHydrated, hydrateSeatPlan]);

	const screenshareRef = useRef(null);

	const lockedUsernames = useMemo(() => {
		const s = new Set();
		for (const u of classroomPolicy.locked_users ?? []) {
			if (u?.username) s.add(u.username);
		}
		return s;
	}, [classroomPolicy.locked_users]);

	const studentScreenshotStatusesForSort = useStudentState((state) => {
		const sid = HackyGetClassroomSessionId(classroom);
		if (!sid) return undefined;
		return state.classSessions[sid];
	});

	const sortedUsers = useMemo(() => {
		if (!showStudentTiles) {
			return [];
		}
		return sortRegularClassroomStudents(users, {
			studentClaims: studentClaimsQuery.data ?? [],
			currentClassStableId: classroom.stableIdV1,
			selectedClassroomStudents,
			studentScreenshotStatuses: studentScreenshotStatusesForSort,
			sortByLastName: true,
			lockedUsernames,
		});
	}, [
		showStudentTiles,
		users,
		classroom,
		studentClaimsQuery.data,
		selectedClassroomStudents,
		studentScreenshotStatusesForSort,
		lockedUsernames,
	]);

	// The seat plan seats the whole roster, but an auto-sorted grid follows this order, so seats stay
	// in the same activity order as the tiles (students with screenshots first). Only while the class
	// runs: screenshots can arrive before it starts, and hoisting those students ahead of the rest
	// makes a stopped class look arbitrarily ordered instead of sorted by name.
	const activityOrderedIds = useMemo(
		() => (isClassActive ? sortedUsers.map((u) => u.qoriaUserId).filter((id) => !!id) : undefined),
		[isClassActive, sortedUsers]
	);

	const use24Hour = useAccountSettingsUse24Hour();

	useEffect(() => {
		setUsers(props.users);
		setClassroom(props.classroom);
		setClassExpiryStr(generateSessionStatusTxt(props.classroom.id, false, use24Hour));
	}, [props, use24Hour]);

	useEffect(() => {
		_.debounce((users) => {
			p2pManager.checkUsersToFetchToken(users);
		}, 3000);
	}, [users]);

	// didMount
	useEffect(() => {
		const isChatEnabled = isChatFeatureEnabled();
		if (isChatEnabled) {
			store.chat.fetchClassOverview({
				classroomId: classroomIdForChat,
			});
		}

		setClassExpiryStr(generateSessionStatusTxt(props.classroom.id, false, use24Hour));
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [props.isClassStarted, use24Hour, classroomIdForChat]);

	// Fetch max end time only once when component mounts
	useEffect(() => {
		// Only fetch if we don't already have the data
		const currentMaxEndTime = useClassroomSessionStore.getState().localisedMaxEndTime;
		if (!currentMaxEndTime) {
			useClassroomSessionStore.getState().fetchAndPopulateMaxEndTime();
		}
	}, []);

	useEffect(() => {
		const onChangeClassroomPolicy = () => {
			setClassExpiryStr(generateSessionStatusTxt(props.classroom.id, false, use24Hour));
			setClassroomPolicy(useClassroomPolicyStore.getState());
			setIsPauseActive(useClassroomPolicyStore.getState().isPauseActive());
			// tabLimit is read directly from store, so component will re-render when it changes
		};

		const onChangeClassroomSessionStore = (state) => {
			setShowStartClassModal(state.showStartClassModal);
			setShowEditClassModal(state.showEditClassModal);
			setStartClassSessionError(state.startClassSessionError);
			setClassExpiryStr(generateSessionStatusTxt(props.classroom.id, false, use24Hour));
		};

		const unsubClassroomSessionStore = useClassroomSessionStore.subscribe(
			(state) => state,
			(newState) => {
				onChangeClassroomSessionStore(newState);
			}
		);

		const unsubClassroomPolicyStore = useClassroomPolicyStore.subscribe(
			(state) => state,
			() => {
				onChangeClassroomPolicy();
			}
		);

		return () => {
			unsubClassroomPolicyStore();
			unsubClassroomSessionStore();
		};
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [features, use24Hour]);

	// Clear Tab Limit when class ends (manual or automatic)
	useEffect(() => {
		// Only clear if transitioning from active to inactive
		if (prevIsClassActiveRef.current === true && isClassActive === false) {
			const currentTabLimit = classroomPolicyStore.getTabLimit();
			if (currentTabLimit !== null) {
				classroomPolicyStore.setTabLimit(null);
			}
		}
		// Update ref for next render
		prevIsClassActiveRef.current = isClassActive;
	}, [isClassActive, classroomPolicyStore]);

	function handleShowPauseDrawer() {
		setShowPauseDrawer(true);
	}

	const handleShowBypassModal = () => {
		if (!focusIsApplied) {
			setShowBypassModal(true);
		}
	};

	const handleShowRewardModal = () => {
		if (!focusIsApplied) {
			setShowRewardModal(true);
		}
	};

	const handleShowPolicyDrawer = () => {
		if (showRulesLibraryDrawer) {
			rulesLibraryDrawer.onOpen();
			return;
		}
		setShowPolicyDrawer(true);
	};

	const endTabLimit = useCallback(() => {
		classroomPolicyStore.setTabLimit(null);
		const teacherUsername = userSessionStore.getActiveUser();
		const applianceId = userSessionStore.getActiveDevice();
		const classroomName = getClassroomDisplayName(classroom);
		if (teacherUsername && applianceId && classroomName) {
			captureTabLimitRemovedEvent(teacherUsername, applianceId, classroomName);
		}
		successToast({
			title: t("classroomDetails.tabLimitEnded"),
			isClosable: true,
		});
	}, [classroomPolicyStore, userSessionStore, classroom, successToast, t]);

	function handleEndPause() {
		unpauseAllStudents(store.classroom.getTeacherStableIdV1(), classroom.stableIdV1)
			.then(() => {
				classroomPolicyStore.setPausedStudents([]);
			})
			.catch(() => {
				errorToast({
					title: t("classroomDetails.failedToEndPause"),
					description: UNEXPECTED_ERROR,
					isClosable: true,
				});
			});
	}

	const handleCloseRulesLibraryDrawer = () => {
		rulesLibraryDrawer.onClose();
		setClassRulePrefill(undefined);
	};

	const onOpenPolicyModalWithUrl = (isEditingRule, websiteUrl, signature, studentUsername) => {
		console.log("onOpenPolicyModalWithUrl", isEditingRule, websiteUrl, signature, studentUsername);
		if (showRulesLibraryDrawer) {
			const student = selectedClassroomStore.findStudent(studentUsername);
			setClassRulePrefill(buildClassRulePrefill(websiteUrl, signature, student?.qoriaUserId));
			rulesLibraryDrawer.onOpen();
			return;
		}

		let hostname;
		if (websiteUrl !== undefined) {
			hostname = url.parse(websiteUrl).hostname;
			if (!hostname) {
				hostname = websiteUrl;
			}
		}

		setShowPolicyModal(true);
		setIsEditingRule(isEditingRule);
		setCurrentEditingRule({
			website: hostname,
			tag: signature,
			permanent: true,
			user: studentUsername,
		});
	};

	const onOpenPolicyModal = (isEditingRule, policy, responseData, type = "SIGNATURE") => {
		setShowPolicyModal(true);
		setIsEditingRule(isEditingRule);
		if (type === "SIGNATURE") {
			setCurrentEditingRule(policy);
			setCurrentEditingNativeAppControl({});
		} else {
			setCurrentEditingRule({});
			setCurrentEditingNativeAppControl(policy);
		}
		setValidationResponseData(responseData);
	};

	const handleShowManageStudentsModal = () => {
		modifyStudentsModal.onOpen();
	};

	const handleShowScreenshareModal = () => {
		setScreenshareModalOpen(true);
	};

	const handleShowFocusDrawer = () => {
		focusLibraryDrawer.onOpen();
	};

	const handleClosePolicyModal = () => {
		setShowPolicyModal(false);
		setIsEditingRule(false);
		setCurrentEditingRule({});
		setValidationResponseData({});
		useClassroomPolicyStore.getState().setLastPolicyValidationResponse(null);
	};

	const toggleShowWindows = () => setShowAllWindows(!showAllWindows);

	const renderBypassActiveButton = () => {
		const isBypassActive = moment().unix() < classroomPolicy.bypass_active_until;
		if (features.indexOf("bypass_code") && !focusIsApplied && isBypassActive) {
			return (
				<Box
					display="flex"
					marginLeft={0}
					columnGap={3}
					title={t("classroomDetails.bypassCode")}
					className="ctxbar_button--green"
					onClick={handleShowBypassModal}
				>
					<ChakraFAIcon icon="fa-key" variant="solid" />
					<span>{t("classroomDetails.bypassCode")}</span>
				</Box>
			);
		}
	};

	const renderRewardActiveButton = () => {
		const isRewardActive = moment().unix() < classroomPolicy.reward_active_until;
		if (features.includes("reward") && !focusIsApplied && isRewardActive) {
			return (
				<Box
					display="flex"
					marginLeft={0}
					columnGap={3}
					title={t("classroomDetails.reward")}
					className={"ctxbar_button--green"}
					onClick={handleShowRewardModal}
				>
					<ChakraFAIcon icon="fa-gift" variant="solid" />
					<span>{t("classroomDetails.reward")}</span>
				</Box>
			);
		}
	};

	const policySize = classroomPolicy.blocked.length + classroomPolicy.allowed.length + classroomPolicy.nativeAppControls.length;
	const rulesButtonCount = showRulesLibraryDrawer ? activeRulesCount : policySize;

	// eslint-disable-next-line @typescript-eslint/no-unused-vars
	const forceCloseTab = (tabId, user, setLoading = false) => {
		if (!features.includes("close_tab")) return;

		const [windowId, tab] = tabId.split("_");

		sendCloseTab(windowId.toString(), tab.toString(), [user.username]);
	};

	const openP2pModal = (user, tabId, peerAgentId, chromeWindowId, followActiveTab = false) => {
		setP2pModalUser(user.username);
		setP2pModalSelectedTab(tabId);
		setP2pAgentId(peerAgentId);
		setP2pChromeWindowId(chromeWindowId);
		setP2pModalOpen(true);
		setP2pModalFollowActiveTab(followActiveTab);
		setP2pModalStudentIsFocused(focusedStudentIds.includes(user.stableIdV1));
	};

	const openOpenSiteModal = (user) => {
		setOpenTabUser(user);
		setShowOpenTabModal(true);
	};

	const closeOpenSiteModal = () => {
		setOpenTabUser(null);
		setShowOpenTabModal(false);
	};

	const sendNewTabModalTitle = () => {
		if (openTabUser) return t("classroomDetails.openTabFor", { name: userDisplayText(openTabUser) });
		return "";
	};

	const closeP2pModal = () => {
		setP2pModalOpen(false);
	};

	const renderP2pModal = () => {
		const user = users.find((user) => user.username === p2pModalUser);
		const window = user.screenshots.find((screen) => screen.chrome_id === p2pAgentId && screen.chrome_window_id === p2pChromeWindowId);
		const platform = windowUtils.getPlatformType(window);
		const chrome = platform === "Chromebook";
		const isStudentClaimed = !getStudentClaimBannerViewModel(user, isClassActive, classroom.stableIdV1, studentClaimsByStudentId).show;

		if (!window || !user || (!p2pModalSelectedTab && chrome)) return;
		if (!chrome && !store.featureToggles.data["agent-p2p-screenshots"]) return;

		const studentIsFocused = p2pModalStudentIsFocused;

		if (chrome) {
			const tabs = [
				{
					tab_id: window.tab_id,
					favIcon: window.favicon,
					tabUrl: window.url,
					title: window.title,
				},
				...window.background_tabs,
			];
			const startTabIndex = tabs.findIndex((tab) => tab.tab_id === p2pModalSelectedTab);

			return (
				<P2PDisplayModal
					closeTab={(tabId, windowId) => forceCloseTab(tabId, windowId, user, false)}
					open={p2pModalOpen}
					close={closeP2pModal}
					user={user}
					startTab={tabs[startTabIndex]}
					startTabIndex={startTabIndex}
					peerAgentId={p2pAgentId}
					activeTab={window}
					tabs={tabs}
					screenshots={user.screenshots}
					openRuleModal={onOpenPolicyModalWithUrl}
					windowId={p2pChromeWindowId}
					classroom={classroom}
					chatDisabled={chatDisabled}
					showOpenTabModal={openOpenSiteModal}
					followActiveTab={p2pModalFollowActiveTab}
					platform={platform}
					studentIsFocused={studentIsFocused}
					isStudentClaimed={isStudentClaimed}
				/>
			);
		} else {
			return (
				<AgentP2PModal
					isMonitoringClass={false}
					isStudentClaimed={isStudentClaimed}
					open={p2pModalOpen}
					close={closeP2pModal}
					user={user}
					activeTab={window}
					windowId={p2pChromeWindowId}
					peerAgentId={p2pAgentId}
					classroom={classroom}
					chatDisabled={chatDisabled}
					platform={platform}
					showOpenTabModal={openOpenSiteModal}
					openRuleModal={onOpenPolicyModalWithUrl}
					studentIsFocused={studentIsFocused}
					screenshots={user.screenshots}
				/>
			);
		}
	};

	let totalWindows = 0;
	let shownWindows = 0;
	let extraWindows = 0;

	const cardsByStudent = {};
	if (showStudentTiles) {
		for (const user of sortedUsers) {
			let ret = [];
			const claimBannerVm = getStudentClaimBannerViewModel(user, isClassActive, classroom.stableIdV1, studentClaimsByStudentId);
			const isStudentClaimBannerLoading = Boolean(claimBannerVm.show && isStudentClaimUiBusy);
			const studentIsClaimedInCurrentClass = studentClaimsByStudentId[user.qoriaUserId]?.classId === classroom.stableIdV1;
			const isStudentClaimReleasable = studentIsClaimedInCurrentClass && (studentClaimsByStudentId[user.qoriaUserId]?.releasable ?? false);
			const uniqueDevices = Array.from(new Set(user.screenshots.map((windowItem) => windowItem.chrome_id))).sort();
			for (const windowData of user.screenshots) {
				ret.push(
					<RegularClassroomUser
						key={user.username + "_" + windowData.chrome_id + "_" + windowData.chrome_window_id}
						deviceIndex={uniqueDevices.indexOf(windowData.chrome_id) + 1}
						user={user}
						windowData={windowData}
						view={view}
						showStudentAnnouncementBtn={features.includes("message_student")}
						showOpenTabBtn={features.includes("open_tab")}
						classIsActive={isClassActive}
						canCloseTab={features.includes("close_tab")}
						classroom={classroom}
						chatDisabled={chatDisabled}
						openP2pModal={openP2pModal}
						openNewTabModal={openOpenSiteModal}
						tabItemLimit={tabItemLimit}
						studentsAreFocused={appliedFocuses.length > 0}
						studentIsFocused={focusedStudentIds.includes(user.stableIdV1)}
						handleToggleFocusForStudent={(studentId) => handleToggleFocusForStudent(studentId, appliedFocuses[0].id)}
						isUpdatingFocus={isUpdatingFocus}
						activeFocusIsLocked={activeFocusIsLocked}
						studentIsBeingScreenshared={screenshareStore.screenshareStudentUsernames.has(user.username)}
						classOpenTime={classOpenTime}
						showStudentClaimBanner={claimBannerVm.show}
						studentClaimBannerTooltip={claimBannerVm.tooltipLabel}
						isStudentClaimBannerLoading={isStudentClaimBannerLoading}
						onStudentClaimBannerClick={() => void handleClaimStudents([user.qoriaUserId ?? ""])}
						isStudentClaimReleasable={isStudentClaimReleasable}
						onStudentClaimReleaseClick={() => void handleReleaseStudentClaim(user.qoriaUserId ?? "")}
					/>
				);
			}

			if (ret.length === 0) {
				ret.push(
					<RegularClassroomUser
						key={user.username}
						user={user}
						windowData={{}}
						view={view}
						showStudentAnnouncementBtn={features.includes("message_student")}
						showOpenTabBtn={features.includes("open_tab")}
						classIsActive={isClassActive}
						classroom={classroom}
						chatDisabled={chatDisabled}
						openNewTabModal={openOpenSiteModal}
						studentsAreFocused={appliedFocuses.length > 0}
						studentIsFocused={focusedStudentIds.includes(user.stableIdV1)}
						handleToggleFocusForStudent={(studentId) => handleToggleFocusForStudent(studentId, appliedFocuses[0].id)}
						isUpdatingFocus={isUpdatingFocus}
						activeFocusIsLocked={activeFocusIsLocked}
						studentIsBeingScreenshared={screenshareStore.screenshareStudentUsernames.has(user.username)}
						classOpenTime={classOpenTime}
						showStudentClaimBanner={claimBannerVm.show}
						studentClaimBannerTooltip={claimBannerVm.tooltipLabel}
						isStudentClaimBannerLoading={isStudentClaimBannerLoading}
						onStudentClaimBannerClick={() => void handleClaimStudents([user.qoriaUserId ?? ""])}
						isStudentClaimReleasable={isStudentClaimReleasable}
						onStudentClaimReleaseClick={() => void handleReleaseStudentClaim(user.qoriaUserId ?? "")}
					/>
				);
			}
			const knownDevices = [];
			let lastUniqueDeviceIndex = 0;
			for (const screenIndex in ret) {
				const currentScreen = ret[screenIndex];
				if (currentScreen.props.deviceIndex && knownDevices.indexOf(currentScreen.props.deviceIndex) < 0) {
					knownDevices.push(currentScreen.props.deviceIndex);
					arrayMove(ret, screenIndex, lastUniqueDeviceIndex);
					lastUniqueDeviceIndex++;
				}
			}
			if (user.qoriaUserId) {
				cardsByStudent[user.qoriaUserId] = ret;
			}
			totalWindows += ret.length;
			if (!showAllWindows && ret.length >= windowLimit) {
				ret = ret.slice(0, windowLimit);
			}
			if (showAllWindows && ret.length >= windowLimit) {
				extraWindows += ret.length - windowLimit;
			}
			shownWindows += ret.length;
		}
	}

	const maxStudents = envConfig.MAX_STUDENTS;

	const onFirstStudentClaimsDetected = useCallback(
		(claims) => {
			const loggedInTeacherUsername = userSessionStore.getActiveUser();
			const applianceId = userSessionStore.getActiveDevice();
			const classroomName = getClassroomDisplayName(classroom);

			for (const claim of claims) {
				captureStudentConflictDetectedEvent({
					applianceId,
					studentUsername: claim.studentUsername,
					managingTeacherUsername: claim.teacherUsername,
					managingTeacherName: claim.teacherName,
					managingClassName: claim.className,
					loggedInTeacherUsername,
					classroomName,
				});
			}
		},
		[userSessionStore, classroom]
	);

	useOnFirstStudentClaimsDetected(classroom.stableIdV1, studentClaimsQuery, onFirstStudentClaimsDetected);

	return (
		<Box>
			<Nav selectedClass={classroom} />
			<TabBar onOpenStudentClaimsModal={() => setStudentClaimsModalOpen(true)} isActive={isClassActive} />
			<StudentClaimsModal
				isOpen={studentClaimsModalOpen}
				onClose={() => setStudentClaimsModalOpen(false)}
				classroomStableId={classroom.stableIdV1}
				onClaimStudents={handleClaimStudents}
			/>

			{!hasClassroomLicense ? (
				<ClassroomPromo />
			) : (
				<ErrorBoundary>
					<ul className="ctxbar--classroomPage">
						<li
							className={
								isClassActive
									? view === "screens"
										? "ctxbar-toggle--active"
										: "ctxbar-toggle track-ScreenView_Active"
									: "ctxbar-toggle-hide"
							}
							onClick={() => setView("screens")}
						>
							{t("classroomDetails.screenView")}
						</li>
						<li
							className={
								isClassActive
									? view === "tabs"
										? "ctxbar-toggle--active--list"
										: "ctxbar-toggle--list track-ListView_Active"
									: "ctxbar-toggle-hide"
							}
							onClick={() => setView("tabs")}
						>
							{t("classroomDetails.listView")}
						</li>

						{isClassActive ? (
							<EditClassButton
								classroomName={classroom.sourceId}
								classExpiryStr={classExpiryStr}
								isMonitoringClass={false}
								screenView={true}
								classActive={isClassActive}
								classroomId={classroom.id}
							>
								<span className="StatusDot Classroom_status" title={t("classroomDetails.classroomActive")} />
								{t("classroomDetails.classUntil", { time: classExpiryStr })}
							</EditClassButton>
						) : (
							<StartClassButton
								classroomName={classroom.sourceId}
								disabled={props.users.length === 0 || props.users.length > maxStudents}
							/>
						)}

						{isClassActive && <EndClassButton classroomName={classroom.sourceId} isMonitoringClass={false} />}

						<Flex columnGap={2} ml={"auto"}>
							{
								<Screenshare
									ref={screenshareRef}
									open={screenshareModalOpen}
									classroomSourceId={activeClassroomSourceId}
									handleClose={() => setScreenshareModalOpen(false)}
									users={users}
									setModalOpen={setScreenshareModalOpen}
									classroomId={classroom.id}
									focusedStudentIds={focusedStudentIds}
									classroomStableId={classroom.stableIdV1}
								/>
							}
							{features.includes("lock") && isPauseActive && (
								<Tooltip label={t("classroomDetails.endPauseForAllStudents")} placement={"top"} hasArrow shouldWrapChildren>
									<ActionButton
										icon={"fa-pause"}
										label={t("classroomDetails.endPause")}
										handleClick={handleEndPause}
										danger
										testId="end-pause-button"
									/>
								</Tooltip>
							)}

							{renderBypassActiveButton()}
							{renderRewardActiveButton()}

							<ReportsDropdown students={users} />

							<ClassToolsDropdown
								openPauseModal={handleShowPauseDrawer}
								openFocusDrawer={handleShowFocusDrawer}
								openRewardModal={handleShowRewardModal}
								openBypassModal={handleShowBypassModal}
								openManageStudentsModal={handleShowManageStudentsModal}
								openScreenshareModal={handleShowScreenshareModal}
								isClassActive={isClassActive}
								areAnyStudentsFocused={focusedStudentIds.length > 0}
								areAllStudentsFocused={focusedStudentIds.length === users.length}
								studentsArePaused={isPauseActive}
								classHasStudents={users.length > 0}
								classSourceId={userSessionStore.getActiveGroup()}
								classroomName={getClassroomDisplayName(classroom)}
								appliedFocuses={appliedFocuses}
								tabLimitModalOpen={tabLimitModalOpen}
								setTabLimitModalOpen={setTabLimitModalOpen}
								tabLimitOpenedViaEdit={tabLimitOpenedViaEdit}
								disabled={isClassToolsDisabled}
								disabledTooltip={classToolsDisabledTooltip}
							/>

							{(showRulesLibraryDrawer || features.includes("policies")) && (
								<ActionButton
									icon={"fa-pen-to-square"}
									label={t("classroomDetails.rulesCount", { count: rulesButtonCount })}
									handleClick={handleShowPolicyDrawer}
									disabled={focusIsApplied || isClassToolsDisabled}
									disabledTooltip={isClassToolsDisabled ? classToolsDisabledTooltip : undefined}
									testId="rules-button"
								/>
							)}

							<ErrorBoundary>
								<ClassroomChat classroom={classroom} chatDisabled={chatDisabled} />
							</ErrorBoundary>
						</Flex>
						<PolicyDrawer
							open={showPolicyDrawer}
							nativeAppControlsDb={nativeAppControlsDb}
							policy={classroomPolicy}
							nativeAppControls={classroomPolicy.nativeAppControls}
							onClose={() => setShowPolicyDrawer(false)}
							onOpenCreatePolicy={onOpenPolicyModal}
							users={users}
						/>
						{showRulesLibraryDrawer && (
							// The drawer can open from the student screen modal, whose StandardModalV2 overlay sits at
							// z-index 100001; this lifts the drawer and its menus, tooltips and confirm modal above it.
							<PortalManager zIndex={100002}>
								<RulesLibraryDrawer
									isOpen={rulesLibraryDrawer.isOpen}
									closeDrawer={handleCloseRulesLibraryDrawer}
									classroomName={getClassroomDisplayName(classroom)}
									classRulePrefill={classRulePrefill}
								/>
							</PortalManager>
						)}
					</ul>

					<FocusAndFlexibleGridBar
						studentCount={props.users.length}
						setTabItemLimit={setTabItemLimit}
						appliedFocuses={appliedFocuses}
						isUpdatingFocus={isUpdatingFocus}
						endFocus={handleEndFocus}
						classIsActive={isClassActive}
						bubbleUpdatedFocus={(focus) => {
							setAppliedFocuses([focus]);
						}}
						tabLimit={tabLimit}
						onEditTabLimit={() => setTabLimitModalOpen(true, true)}
						onEndTabLimit={endTabLimit}
						isUpdatingTabLimit={false}
						activityOrderedIds={activityOrderedIds}
					/>

					<ClassroomToolsContext.Provider value={{ openRuleModal: onOpenPolicyModalWithUrl }}>
						{(seatRosterStudents ?? []).length === 0 ? (
							<SeatPlanEmptyState />
						) : showStudentTiles && isSeatPlanHydrated ? (
							<SeatPlanView
								cardsByStudent={cardsByStudent}
								isClassActive={isClassActive}
								activityOrderedIds={activityOrderedIds}
								students={(seatRosterStudents ?? []).map((s) => ({
									qoriaUserId: s.qoriaUserId,
									stableIdV1: s.stableIdV1,
									username: s.username,
									firstName: s.firstName,
									lastName: s.lastName,
								}))}
								onStartScreenshare={(usernames) => {
									void screenshareRef.current?.addUsers(usernames);
								}}
								onEndScreenshare={() => screenshareRef.current?.endScreenshare()}
								onFocusApplied={() => fetchAppliedFocuses()}
								hasActiveFocus={appliedFocuses.length > 0}
								focusedStudentIds={focusedStudentIds}
								onAddStudentsToActiveFocus={handleAddStudentsToActiveFocus}
								onRemoveStudentsFromActiveFocus={handleRemoveStudentsFromActiveFocus}
								onReleaseClaim={(studentId) => void handleReleaseStudentClaim(studentId)}
							/>
						) : null}
					</ClassroomToolsContext.Provider>

					<CreatePolicy
						open={showPolicyModal}
						nativeAppControlsDb={nativeAppControlsDb}
						editing={isEditingRule}
						policy={currentEditingRule}
						nativeAppControl={currentEditingNativeAppControl}
						onClose={handleClosePolicyModal}
						validationResponseData={lastPolicyValidationResponse ?? validationResponseData}
						users={users}
					/>
					<RewardModal open={showRewardModal} onClose={() => setShowRewardModal(false)} />
					{showBypassModal && <BypassModal onClose={() => setShowBypassModal(false)} />}
					<PauseInternetDrawer
						isOpen={showPauseDrawer}
						closeDrawer={() => setShowPauseDrawer(false)}
						classId={classroom.stableIdV1}
						students={users}
						pausedStudentIds={classroomPolicy.locked_users}
					/>

					<ClassroomStartSession open={showStartClassModal} onStartError={startClassSessionError} inClassroom studentCount={users.length} />

					<ClassroomEditSession
						open={showEditClassModal}
						onExtendError={startClassSessionError}
						durationPlaceholder={classExpiryStr}
						inClassroom
					/>

					<SendNewTabModal
						open={showOpenTabModal}
						title={sendNewTabModalTitle()}
						classroomSourceId={activeClassroomSourceId}
						users={[openTabUser]}
						onClose={closeOpenSiteModal}
					/>

					<ModifyStudentsModal
						isOpen={modifyStudentsModal.isOpen}
						onClose={modifyStudentsModal.onClose}
						classroom={classroom}
						students={users}
						isMonitoringClass={false}
						// When a student is removed from a class, any focus applied to them is lifted.
						// If they are re-added to the class, the teacher will need to reapply the focus.
						// This is not reflected on the UI unless we refresh the applied focuses - the student
						// will appear focused when they are not until the state held by the UI is updated.
						studentsModifiedCallback={() => fetchAppliedFocuses()}
						handleRefreshStudentClaims={handleRefreshStudentClaims}
					/>

					<FocusLibraryDrawer
						bubbleAppliedOrUpdatedFocus={(focusDto) => {
							const allSignatureResourceOptions = getSignaturesForFocusResourcePicker(signatures);
							setAppliedFocuses([...appliedFocuses, transformFocusDtoToEditingFocus(focusDto, allSignatureResourceOptions)]);
						}}
						isOpen={focusLibraryDrawer.isOpen}
						closeDrawer={focusLibraryDrawer.onClose}
						classIsActive={isClassActive}
					/>

					<ConfirmEndFocusModal
						isOpen={confirmEndFocusModal.isOpen}
						onClose={confirmEndFocusModal.onClose}
						focusName={confirmEndingFocusName}
						handleConfirmation={() => handleEndFocus(appliedFocuses[0].id)}
					/>

					{p2pModalOpen && renderP2pModal()}
					{totalWindows - shownWindows > 0 || extraWindows > 0 ? (
						<div className="RegularClassroom_showAllWindowsButton" onClick={toggleShowWindows}>
							{showAllWindows
								? t("classroomDetails.hideExtraScreens", { count: extraWindows })
								: t("classroomDetails.showExtraScreens", { count: totalWindows - shownWindows })}
						</div>
					) : null}
				</ErrorBoundary>
			)}
		</Box>
	);
};

RegularClassroom.propTypes = {
	users: PropTypes.array.isRequired,
	classroom: PropTypes.object,
	router: PropTypes.object,
	isClassStarted: PropTypes.bool,
};
